import React, { useCallback, useEffect, useState, useMemo } from 'react'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import Api from '../../../api'
import TalentNavbar from '../../../components/TalentNavbar'
import TalentPageLayout from '../../../components/TalentPageLayout'
import TalentBackButton from '../../../components/TalentBackButton/TalentBackButton'
import FixedLoader from '../../../components/FixedLoader'
import { Page, Container, Header, SortLabel, SortButtons, SortButton, ListGrid, ListItem, ItemButton, ItemIcon, ItemTexts, ItemTitle, ItemSubtitle, Chevron, EmptyState, Loading } from './JobHistoryPage.styles'

type SortKey = 'date' | 'office'

interface HygienistJob {
  id: number
  status: number
  practiceName?: string
  startDate?: string
}

const JobHistoryPage: React.FC = () => {
  const history = useHistory()
  const [jobs, setJobs] = useState<HygienistJob[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [sortBy, setSortBy] = useState<SortKey>('date')
  const [userLoading, setUserLoading] = useState<boolean>(true)
  const [isDentist, setIsDentist] = useState<boolean>(false)

  // Get current user info for navbar
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.()
  const hygienist = currentUser?.hygienist
  const firstName = hygienist?.firstName || ''
  const lastName = hygienist?.lastName || ''
  const photoUrl = hygienist?.photoUrl || undefined
  const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST || 
                      hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT

  useEffect(() => {
    let cancelled = false
    const checkUser = async () => {
      try {
        setUserLoading(true)
        const user = await Api.getUser()
        if (cancelled) return
        setIsDentist(!!user?.dentist && !user?.hygienist)
      } catch (e) {
        setIsDentist(false)
      } finally {
        if (!cancelled) setUserLoading(false)
      }
    }
    checkUser()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const all = await Api.getHygienistJobs('')
        if (cancelled) return
        const completed = (all || []).filter((j: HygienistJob) => Number(j.status) === 4)
        setJobs(completed)
      } catch (e) {
        setJobs([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const sortedJobs = useMemo(() => {
    const list = [...jobs]
    if (sortBy === 'date') {
      list.sort((a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf())
    } else {
      list.sort((a, b) => (a.practiceName || '').localeCompare(b.practiceName || ''))
    }
    return list
  }, [jobs, sortBy])

  const onOpenJob = useCallback((jobId: number) => {
    history.push({ pathname: 'completeJob', state: { jobId } })
  }, [history])

  const handleBack = useCallback(() => {
    if (isDentist) {
      history.push({ pathname: 'dentistHome' })
    } else {
      history.push({ pathname: 'hygienistHome' })
    }
  }, [history, isDentist])

  const handleSortChange = useCallback((key: SortKey) => {
    setSortBy(key)
  }, [])

  return (
    <>
      <TalentPageLayout
        navbar={(
          <TalentNavbar
            firstName={firstName}
            lastName={lastName}
            photoUrl={photoUrl}
            isHygOrAsst={isHygOrAsst}
            instabookActivated={hygienist?.InstabookActivated === 1}
            isTestAccount={(hygienist as any)?.isTest === 1}
            showAddShiftButton={false}
            showJobBoardButton={false}
          />
        )}
      >
      <Page>
        <TalentBackButton onBack={handleBack} />
        <Container>
          <h1 className="pageTitle">Job History</h1>
          <p className="pageSubtitle">View and manage your past assignments</p>

          <Header>
          <SortLabel>Sort by</SortLabel>
          <SortButtons>
            <SortButton $active={sortBy === 'date'} onClick={() => handleSortChange('date')}>Shift Date</SortButton>
            <SortButton $active={sortBy === 'office'} onClick={() => handleSortChange('office')}>Office</SortButton>
          </SortButtons>
        </Header>

        {loading ? (
          <Loading>Loading jobs…</Loading>
        ) : (
          sortedJobs.length === 0 ? (
            <EmptyState>No completed jobs.</EmptyState>
          ) : (
            <ListGrid>
              {sortedJobs.map(job => (
                <ListItem key={job.id}>
                  <ItemButton onClick={() => onOpenJob(job.id)}>
                    <ItemIcon aria-hidden>
                      <BusinessOutlinedIcon sx={{ fontSize: 22, color: '#0B4598' }} />
                    </ItemIcon>
                    <ItemTexts>
                      <ItemTitle>{job.practiceName || 'Office'}</ItemTitle>
                      <ItemSubtitle>Shift Date: {job.startDate ? dayjs(job.startDate).format('MMM D, YYYY') : ''}</ItemSubtitle>
                    </ItemTexts>
                    <Chevron className="ti-angle-right" aria-hidden="true" />
                  </ItemButton>
                </ListItem>
              ))}
            </ListGrid>
          )
        )}
        </Container>
      </Page>
      </TalentPageLayout>
      
      <FixedLoader loading={loading || userLoading} />
    </>
  )
}

export default JobHistoryPage


